import type { ReactNode } from 'react';
import { AppIcon } from '@/components/AppIcon';
import type { DesktopAction } from '@/components/desktop';
import { DocumentFrame } from '@/components/DocumentFrame';
import { SavedWorkDetail } from '@/components/SavedWorkDetail';
import { ErrorNote, OkNote } from '@/components/ui';
import { localId, type Operation } from '@/offline/outbox';
import { piecesOf } from '@/offline/pieces';
import { useSavedWork } from '@/offline/useSavedWork';

/**
 * An entry of the Items Manager on its own screen, opened with one tap on its
 * row, as Stock IN and Stock OUT open theirs. These lists have no "fetch one"
 * on the server, so the page shows the row it was opened from, kept fresh
 * from the list while the list still holds it.
 */

export type Notice = { tone: 'ok' | 'error'; message: string } | null;

/** The frame of an entry's page: what it is, its status, and what can be done to it. */
export function RecordPage({
  backLabel,
  onBack,
  icon,
  code,
  name,
  nameUr,
  status,
  actions,
  notice,
  children,
}: {
  backLabel: string;
  onBack: () => void;
  icon: ReactNode;
  code: string;
  name: string;
  nameUr?: string | null | undefined;
  status: ReactNode;
  /** The row's own actions; Open is left out, the page being it. */
  actions: DesktopAction[];
  notice: Notice;
  children: ReactNode;
}) {
  return (
    <DocumentFrame
      onBack={onBack}
      backLabel={backLabel}
      title={
        <>
          <h2 className="withicon">
            {icon}
            <span>
              <span className="mono">{code}</span> {name}
              {nameUr && <span className="urdu"> · {nameUr}</span>}
            </span>
          </h2>
          {status}
        </>
      }
      actions={actions
        .filter((a) => a.allowed && a.label !== 'Open')
        .map((a) => (
          <button
            key={a.label}
            type="button"
            className={`btn btn--withicon${a.danger ? ' btn--danger' : ''}`}
            disabled={a.enabled === false}
            title={a.enabled === false ? a.reason : undefined}
            onClick={a.onSelect}
          >
            {a.icon && <AppIcon kind={a.icon} size={15} />}
            {a.label}
          </button>
        ))}
    >
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}
      {children}
    </DocumentFrame>
  );
}

/**
 * An entry added on this PC that the server has not made yet (.docs/023),
 * opened from its "Not sent yet" row: what was saved, and where it has got to.
 * Once sent, "Open" moves to the entry the server made.
 */
export function PendingRecordPage({
  rowId,
  backLabel,
  onBack,
  onFix,
  onOpen,
  onDone,
}: {
  /** The pending row's id: `local:` and the operation's. */
  rowId: string;
  backLabel: string;
  onBack: () => void;
  onFix: (op: Operation) => void;
  /** The server's id for it, once made. */
  onOpen: (id: string) => void;
  onDone: (said: string) => void;
}) {
  const { data } = useSavedWork();
  const all = data ?? [];
  const piece = piecesOf(all).find((p) => p.ops.some((op) => localId(op.id) === rowId));
  if (!piece) {
    // Thrown away, or sent and since forgotten: nothing left to show here.
    return (
      <DocumentFrame onBack={onBack} backLabel={backLabel}>
        <p className="muted">This is no longer saved on this PC. Go back to the list to find it.</p>
      </DocumentFrame>
    );
  }
  return (
    <SavedWorkDetail
      piece={piece}
      all={all}
      backLabel={backLabel}
      onBack={onBack}
      onFix={onFix}
      onOpenDocument={onOpen}
      onDone={onDone}
    />
  );
}

/** Said at the top of a deleted entry's page: it is switched off, not gone. */
export function DeletedNote({ what }: { what: string }) {
  return (
    <p className="docpage__note docpage__note--alert" role="note">
      This {what} is deleted. It was used, so it is switched off and its history stays. Reactivate brings it back.
    </p>
  );
}
